// Spanish content for Lawyah — mirrors the exports of mock.js
// Note: dashboard visual data (funnel, ledger, integrations) stays in English on purpose,
// like real product screenshots.

export const BRAND = "Lawyah";

export const NAV_LINKS = [
  { label: "Cómo funciona", path: "/how-it-works" },
  { label: "Marketing", path: "/marketing" },
  { label: "Precios", path: "/pricing" },
  { label: "Preguntas", path: "/faq" },
];

export const HERO = {
  eyebrow: "Una capa sobre MyCase",
  title: ["Nunca pierdas un cliente.", "Siempre sabrás", "cuánto vale."],
  subtitle:
    "Lawyah se integra con el sistema de gestión que tu bufete ya utiliza. Nuestro agente de IA responde cada llamada y chat las 24 horas, un panel en vivo muestra los ingresos por canal, y cada cliente potencial se sincroniza directamente con MyCase — nada que migrar, nada que reaprender.",
  stats: [
    { value: "24/7", label: "atención telefónica y por chat, incluso fuera de horario" },
    { value: "6", label: "canales de marketing sincronizados en un solo panel" },
  ],
};

export const LOGO_STRIP = {
  text: "Funciona sobre los sistemas que los bufetes de litigios, derecho familiar y lesiones personales ya utilizan",
  logos: ["MyCase", "Google LSA", "Google Business Profile", "Google Ads", "Meta Ads", "Dialpad"],
};

export const PROBLEMS = {
  eyebrow: "El problema",
  title: "La mayoría de los bufetes pierden casos antes de que lleguen a un abogado",
  items: [
    {
      title: "Las llamadas fuera de horario van al buzón de voz",
      body: "Un cliente potencial llama a las 9 de la noche tras un accidente de coche o un arresto. Si nadie contesta, llama al siguiente bufete de la lista — y ese bufete firma el caso.",
    },
    {
      title: "El gasto en marketing repartido en cinco paneles distintos",
      body: "Google Ads, Local Services Ads, Meta, Google Business Profile y MyCase reportan sus propios números — pero nadie puede decir qué canal produce realmente clientes que firman y pagan.",
    },
    {
      title: "La admisión dispersa en notas adhesivas y correos",
      body: "Nombres, detalles del caso y verificaciones de conflictos viven entre transcripciones de buzón de voz, una bandeja compartida y quien haya contestado el teléfono ese día.",
    },
  ],
  fix: {
    eyebrow: "La solución",
    title: "Un sistema que responde, califica y demuestra que valió la pena",
    body: "Lawyah pone un especialista de admisión con IA en cada llamada y chat, ejecuta la verificación de conflictos automáticamente y deposita un registro limpio directamente en MyCase — con el canal de marketing adjunto, cada vez.",
  },
};

export const STEPS = {
  eyebrow: "Cómo funciona",
  title: "Del primer timbre al contrato firmado",
  subtitle:
    "Cuatro pasos, funcionando continuamente, sin huecos por noches, fines de semana o una recepción que ya está en otra línea.",
  items: [
    {
      num: "1",
      title: "El cliente llama o chatea — a cualquier hora",
      body: "Lawyah atiende cada llamada entrante y chat web al instante, de día o de noche, con una voz conversacional natural o por texto.",
    },
    {
      num: "2",
      title: "Lawyah captura los detalles y verifica conflictos",
      body: "Nombre, tipo de caso, parte contraria y hechos clave se recogen en una admisión estructurada, y la verificación de conflictos de interés se ejecuta automáticamente.",
    },
    {
      num: "3",
      title: "Sincronizado al instante con MyCase",
      body: "La admisión terminada, la grabación y la transcripción llegan a tu expediente de MyCase en segundos — sin volver a escribir, sin notas perdidas.",
    },
    {
      num: "4",
      title: "Ves la atribución y das seguimiento",
      body: "El panel muestra de qué canal vino el cliente y su valor probable, para que tu equipo dé seguimiento primero a los casos que más valen.",
    },
  ],
};

export const PRODUCT_SECTIONS = {
  agent: {
    eyebrow: "Lawyah, el agente de admisión con IA",
    title: "Una recepción que nunca duerme, nunca se enferma y nunca olvida preguntar por conflictos",
    body: "Lawyah gestiona la admisión por teléfono y chat web con la misma disciplina estructurada cada vez — ningún detalle depende de quién estuviera en el escritorio cuando entró la llamada.",
    bullets: [
      "Atención telefónica 24/7 con conversación natural por voz",
      "Admisión por chat web con el mismo flujo estructurado",
      "Verificación automática de conflictos de interés antes de aceptar un caso",
      "Cero clientes perdidos — cada llamada es atendida, siempre",
    ],
  },
  dashboards: {
    eyebrow: "Paneles e integraciones",
    title: "Un solo panel para cada canal que ya pagas",
    body: "MyCase, Google Local Services Ads, Google Business Profile, Google Ads, Meta y tu sistema telefónico reportan a una sola vista de Lawyah — se acabó entrar a seis consolas distintas para entender qué pasó esta semana.",
    bullets: [
      "Sincronización nativa bidireccional con MyCase",
      "Integración con Google Local Services Ads (LSA)",
      "Google Business Profile, Google Ads y Meta, en la misma vista",
      "Integración telefónica para clientes por llamada",
    ],
    integrations: [
      { name: "MyCase", status: "Connected" },
      { name: "Google LSA", status: "Connected" },
      { name: "Google Business Profile", status: "Connected" },
      { name: "Google Ads", status: "Connected" },
      { name: "Meta Ads", status: "Connected" },
      { name: "Dialpad", status: "Connected" },
    ],
  },
  revenue: {
    eyebrow: "Ingresos por canal",
    title: "Ve los ingresos por canal — no solo cuál recibe clics",
    body: "Cada cliente potencial lleva su origen desde la admisión hasta el contrato firmado, así que el costo por lead se convierte en costo por cliente, y por fin puedes saber cuánto vale realmente tu inversión en marketing.",
    bullets: [
      "Costo por lead y costo por caso firmado, por canal",
      "Desglose por UTM, campaña y fuente de referencia",
      "Retorno de la inversión publicitaria, calculado automáticamente",
      "Ingresos conciliados contra tus propios números de MyCase",
    ],
    channels: [
      { name: "Google LSA", value: "$18.4k", pct: 100 },
      { name: "Google Ads", value: "$12.9k", pct: 70 },
      { name: "Meta Ads", value: "$9.6k", pct: 52 },
      { name: "Referrals", value: "$7.1k", pct: 39 },
    ],
    roas: { value: "4.6x", label: "retorno del gasto en LSA, este mes" },
  },
  tracking: {
    eyebrow: "Seguimiento de leads",
    title: "Observa cada cliente potencial moverse, en tiempo real",
    body: "Un embudo en vivo y un feed de leads recientes dan a los socios una lectura diaria del pipeline, desde el primer contacto hasta el contrato firmado — sin esperar el reporte de fin de mes.",
    bullets: [
      "Consulta → admisión → consulta pagada → contrato firmado",
      "Tendencias diarias y semanales por área de práctica",
      "Feed de leads recientes, visible para todo el equipo",
    ],
    funnel: [
      { stage: "Enquiry", count: 248, pct: 100 },
      { stage: "Intake completed", count: 201, pct: 81 },
      { stage: "Paid consult", count: 96, pct: 39 },
      { stage: "Signed retainer", count: 58, pct: 23 },
    ],
    recent: [
      { name: "M. Okafor — auto accident", status: "Signed · 4m ago" },
      { name: "J. Park — custody modification", status: "Consult set · 22m ago" },
      { name: "R. Alvarez — slip and fall", status: "Intake · 41m ago" },
    ],
  },
  mycase: {
    eyebrow: "Funciona con MyCase",
    title: "Nada que desinstalar, nada que reaprender",
    body: "Lawyah no es otro sistema que tu equipo tenga que aprender desde cero — se integra con tu cuenta de MyCase existente. Expedientes, actividad del libro fiduciario y documentos permanecen exactamente donde tu equipo ya los busca.",
    bullets: [
      "Sincronización bidireccional con expedientes y campos de admisión de MyCase",
      "Actividad fiduciaria reflejada en un registro inmutable",
      "Conserva tus accesos, facturación y portal de clientes de MyCase",
      "Empieza sin migrar un solo expediente",
    ],
    ledger: [
      { entry: "Retainer deposit", date: "Sep 2, 10:14 AM", matter: "Delgado v. Reyes", balance: "$5,000.00" },
      { entry: "Filing fee disbursed", date: "Sep 3, 2:41 PM", matter: "Delgado v. Reyes", balance: "$4,637.00" },
      { entry: "Retainer deposit", date: "Sep 4, 9:02 AM", matter: "Whitfield estate", balance: "$3,200.00" },
    ],
  },
  compliance: {
    eyebrow: "Cumplimiento y seguridad",
    title: "Diseñado para las exigencias de cumplimiento IOLTA — no añadido después",
    body: "Un error en la contabilidad fiduciaria puede costarle la licencia a un abogado. Cada cuenta, expediente y dólar está aislado por bufete, y cada sincronización entre Lawyah y MyCase queda registrada permanentemente, con quién la hizo y cuándo.",
    bullets: [
      "Seguridad a nivel de fila en Postgres — aislamiento total por bufete",
      "Permisos por rol y alcance para cada usuario",
      "Autenticación OAuth 2.0 en todo el sistema",
      "Registro de auditoría completo e inmutable en cada movimiento de dinero",
    ],
    audit: [
      "09:14:02 disbursement · $363.00 · Delgado v. Reyes · by A. Reyna",
      "09:41:57 retainer deposit · $3,200.00 · Whitfield estate · by system",
    ],
  },
};

export const MARKETING = {
  eyebrow: "Marketing gestionado",
  title: "Gestionamos tu marketing — anuncios en Google, Meta, web abierta y OpenAI",
  subtitle:
    "Lawyah no solo mide tus canales: los gestionamos. Nuestro equipo planifica, lanza y optimiza campañas en todas las redes principales — y cada dólar invertido se vincula a contratos firmados en tu panel.",
  channels: [
    {
      name: "Google Ads y LSA",
      tag: "Intención de búsqueda",
      body: "Campañas de búsqueda, Local Services Ads y Google Business Profile — captando clientes en el momento exacto en que buscan un abogado.",
      points: ["Gestión de Local Services Ads", "Campañas de Search y Performance Max", "Optimización de Google Business Profile"],
    },
    {
      name: "Meta Ads",
      tag: "Alcance social",
      body: "Campañas en Facebook e Instagram que mantienen tu bufete frente a tu comunidad — con retargeting automático de visitantes y consultas anteriores.",
      points: ["Campañas en Facebook e Instagram", "Retargeting y audiencias similares", "Creatividades adaptadas al cumplimiento legal"],
    },
    {
      name: "Web abierta y programática",
      tag: "Escala display",
      body: "Display programático y anuncios nativos en la web abierta, ajustados a tus áreas de práctica y geografía — con precio por rendimiento, no por impresiones.",
      points: ["Display programático y anuncios nativos", "Geolocalizado a tu zona de servicio", "Ubicaciones seguras y verificadas legalmente"],
    },
    {
      name: "Anuncios en OpenAI",
      tag: "Descubrimiento IA",
      body: "A medida que los clientes piden recomendaciones de abogados a asistentes de IA, posicionamos tu bufete en las superficies de descubrimiento con IA — el canal más nuevo, medido como todos los demás.",
      points: ["Presencia en respuestas de IA", "Ventaja de pionero en búsqueda con IA", "Misma atribución, mismo panel"],
    },
  ],
  stats: [
    { value: "6", label: "redes publicitarias gestionadas bajo un mismo techo" },
    { value: "100%", label: "del gasto atribuido a casos firmados" },
    { value: "1", label: "panel para todos los canales" },
  ],
  how: {
    title: "Gestionado como una agencia, medido como software",
    body: "La mayoría de las agencias reportan clics. Nosotros reportamos contratos firmados. Como Lawyah gestiona tu admisión, cada campaña se evalúa por el único número que importa: los ingresos.",
    points: [
      "Gestor de campañas dedicado para tu bufete",
      "Revisiones estratégicas mensuales basadas en datos de ingresos",
      "Los presupuestos se mueven automáticamente hacia los canales que firman casos",
      "Sin permanencia — te retiene el rendimiento, no un contrato",
    ],
  },
};

export const PRICING = {
  eyebrow: "Precios",
  title: "Planes claros, a la medida de tu bufete",
  subtitle:
    "Todos los planes incluyen admisión 24/7 y sincronización con MyCase. El precio es por bufete, no por funciones que desbloquear después — habla con nosotros y lo ajustamos a tu volumen de llamadas.",
  plans: [
    {
      name: "Starter",
      desc: "Abogados independientes y equipos pequeños saliendo de las hojas de cálculo",
      price: "A medida",
      period: "/ mes",
      cta: "Habla con nosotros",
      featured: false,
      features: [
        "Admisión por teléfono y chat 24/7 con Lawyah",
        "Verificación de conflictos y admisión estructurada",
        "Sincronización con MyCase y espejo de documentos",
        "Hasta 3 usuarios",
      ],
    },
    {
      name: "Growth",
      desc: "Bufetes establecidos que necesitan atribución y contabilidad fiduciaria",
      price: "A medida",
      period: "/ mes",
      cta: "Reservar demo",
      featured: true,
      badge: "Más popular",
      features: [
        "Todo lo de Starter",
        "Contabilidad fiduciaria IOLTA con registro inmutable",
        "Paneles de marketing e integraciones (LSA, GBP, Google Ads, Meta)",
        "Usuarios ilimitados",
      ],
    },
    {
      name: "Enterprise",
      desc: "Bufetes con varias oficinas y necesidades dedicadas de cumplimiento",
      price: "A medida",
      period: "/ mes",
      cta: "Habla con nosotros",
      featured: false,
      features: [
        "Todo lo de Growth",
        "Soporte multi-oficina y multi-entidad",
        "Soporte dedicado de cumplimiento y onboarding",
        "SLAs personalizados y opciones de residencia de datos",
      ],
    },
  ],
};

export const DEMO = {
  eyebrow: "Reservar una demo",
  title: "Ve Lawyah con tus propios clientes potenciales",
  subtitle:
    "Veinte minutos, en una pantalla compartida, con tus tipos de llamadas y casos reales — no una presentación genérica.",
  points: [
    "Recorreremos una llamada de admisión en vivo, de principio a fin",
    "Mapearemos tus canales actuales en el panel de ingresos",
    "Sin presión, sin compromiso, sin tarjeta de crédito",
  ],
};

export const FAQS = {
  eyebrow: "Preguntas frecuentes",
  title: "Lo que los bufetes preguntan antes de cambiarse",
  subtitle: "¿No ves la tuya? Pregúntanos directamente en una llamada de demo.",
  items: [
    {
      q: "¿Dónde viven nuestros datos y qué tan seguros están?",
      a: "Los datos de tu bufete están aislados con seguridad a nivel de fila en Postgres, de modo que las filas de otro bufete nunca son accesibles a través de la aplicación, incluso ante un error. El acceso usa OAuth 2.0 y permisos por alcance, y todos los datos se cifran en tránsito y en reposo.",
    },
    {
      q: "¿Cómo maneja Lawyah el cumplimiento IOLTA?",
      a: "Cada entrada del libro fiduciario se refleja en un registro inmutable, con marca de tiempo y atribuida a la persona o sistema que la hizo. Nada puede editarse después de publicarse, así que tu registro de auditoría está completo por construcción.",
    },
    {
      q: "¿Tenemos que dejar MyCase?",
      a: "No. Lawyah se integra con tu cuenta de MyCase existente con sincronización nativa bidireccional. Tus expedientes, facturación, accesos y portal de clientes se quedan exactamente donde están — nada que migrar.",
    },
    {
      q: "¿Qué pasa con nuestro personal de admisión actual?",
      a: "Lawyah maneja el volumen que ningún equipo humano puede: noches, fines de semana y llamadas simultáneas. Tu personal sube en la cadena de valor: revisando admisiones calificadas, agendando consultas y dando seguimiento a los casos más valiosos primero.",
    },
    {
      q: "¿Quién gestiona nuestras campañas publicitarias?",
      a: "Nosotros. Nuestro equipo gestiona tus campañas de Google, Meta, web abierta y OpenAI de principio a fin, con un gestor dedicado y revisiones estratégicas mensuales — y cada dólar se atribuye a casos firmados en tu panel.",
    },
    {
      q: "¿Cómo se cobra Lawyah?",
      a: "Por bufete, según tu volumen de llamadas y mezcla de canales — no por función. Todos los planes incluyen admisión 24/7 y sincronización con MyCase. Reserva una demo y te cotizamos tu configuración exacta.",
    },
    {
      q: "¿Cuánto tarda la implementación?",
      a: "La mayoría de los bufetes están operando en menos de una semana. Como Lawyah se integra con MyCase en vez de reemplazarlo, no hay migración de datos: conectamos tus canales, configuramos tu flujo de admisión y empiezas a atender cada llamada.",
    },
  ],
};

export const FOOTER = {
  blurb: "La capa de admisión con IA e ingresos para bufetes de abogados. Responde cada cliente, demuestra cada dólar.",
  contact: {
    email: "info@stratavaglobal.com",
    phone: "+1 (555) 210-4400",
    address: "Con cita previa — atendemos bufetes en todo el país",
  },
  columns: [
    {
      title: "Explorar",
      links: [
        { label: "Cómo funciona", path: "/how-it-works" },
        { label: "Marketing", path: "/marketing" },
        { label: "Precios", path: "/pricing" },
      ],
    },
    {
      title: "Empresa",
      links: [
        { label: "Reservar demo", path: "/book-demo" },
        { label: "Preguntas", path: "/faq" },
        { label: "Bandeja de leads", path: "/admin" },
      ],
    },
  ],
  legal: [
    { label: "Política de privacidad", path: "/privacy" },
    { label: "Términos del servicio", path: "/terms" },
  ],
};

export const UI = {
  nav: { login: "Iniciar sesión", bookDemo: "Reservar demo" },
  home: {
    bookDemo: "Reservar demo",
    seeHow: "Ver cómo funciona",
    fullWalkthrough: "Ver el recorrido completo",
    exploreMarketing: "Explorar marketing gestionado",
    ctaTitle: "Cada llamada atendida. Cada dólar justificado.",
    ctaText: "Ve Lawyah con tus propios clientes potenciales — veinte minutos, sin diapositivas, sin compromiso.",
    viewPricing: "Ver precios",
  },
  hiw: {
    step: "Paso",
    ctaTitle: "Sin huecos. Sin noches libres. Sin clientes perdidos.",
    ctaText: "Todo el ciclo funciona continuamente — mientras las llamadas de tus competidores van al buzón de voz, las tuyas van a Lawyah.",
  },
  marketing: {
    fullService: "Servicio completo",
    talkTeam: "Habla con nuestro equipo de marketing",
    attributionTitle: "Gasto → casos firmados, este mes",
    signed: "firmados",
    signedSummary: "31 contratos firmados",
    signedSummaryLabel: "atribuidos entre todos los canales gestionados",
  },
  pricing: {
    note: "Todos los planes incluyen admisión 24/7, sincronización con MyCase y soporte de onboarding. El marketing gestionado está disponible como complemento en cualquier plan.",
  },
  demo: {
    formTitle: "Solicitar una demo",
    formSubtitle: "Respondemos en un día hábil.",
    labels: { full_name: "Nombre completo", firm_name: "Nombre del bufete", email: "Correo de trabajo", phone: "Teléfono" },
    submit: "Solicitar mi demo",
    submitting: "Enviando…",
    disclaimer: "Al enviar, aceptas ser contactado sobre Lawyah. Nunca spam.",
    successTitle: "Solicitud recibida",
    successText: "Gracias, {name}. Te responderemos en un día hábil para agendar tu demo.",
    submitAnother: "Enviar otra solicitud",
    whatTitle: "Lo que verás",
    whatItems: [
      "Lawyah atendiendo una llamada de admisión en vivo, de principio a fin",
      "Tus canales mapeados en el panel de ingresos",
      "El plan de marketing gestionado para tus áreas de práctica",
    ],
    toastSuccess: "Solicitud recibida — te responderemos en un día hábil.",
    toastError: "Algo salió mal. Inténtalo de nuevo.",
    toastValidation: "Por favor completa tu nombre y correo de trabajo.",
  },
  faq: {
    stillTitle: "¿Todavía tienes una pregunta?",
    stillText: "Pregúntanos directamente — o pregúntale a Lawyah en el chat, a cualquier hora.",
    stillCta: "Reservar una llamada de demo",
  },
  footer: { contact: "Contacto", rights: "Todos los derechos reservados." },
  chat: {
    title: "Lawyah — Admisión IA",
    online: "En línea · responde al instante",
    welcome:
      "Hola, soy Lawyah — el agente de admisión con IA. Puedo contarte cómo respondemos cada llamada 24/7, sincronizamos leads con MyCase y gestionamos tu marketing en Google, Meta, web abierta y anuncios de OpenAI. ¿Qué te gustaría saber?",
    placeholder: "Pregunta sobre admisión, precios, marketing…",
    error: "Lo siento, tuve un problema de conexión. Inténtalo de nuevo en un momento.",
  },
};

export const LEGAL = {
  contactLabel: "¿Preguntas? Escríbenos a",
  privacy: {
    eyebrow: "Legal",
    title: "Política de privacidad",
    subtitle: "Cómo Lawyah recopila, usa y protege tu información.",
    updated: "Última actualización: julio 2025",
    sections: [
      {
        title: "Información que recopilamos",
        body: "Cuando solicitas una demo o chateas con nuestro asistente de IA, recopilamos los datos que decides compartir — como tu nombre, nombre del bufete, correo de trabajo, teléfono y el contenido de tu conversación.",
      },
      {
        title: "Cómo la usamos",
        body: "Usamos esta información para responder a tu consulta, agendar demos y mejorar nuestro producto. No vendemos tu información personal a terceros.",
      },
      {
        title: "Seguridad de los datos",
        body: "Los datos se cifran en tránsito y en reposo. Los datos de cada bufete están aislados por inquilino, y el acceso se controla con permisos por alcance y autenticación OAuth 2.0.",
      },
      {
        title: "Tus opciones",
        body: "Puedes solicitar acceso, corrección o eliminación de tu información personal en cualquier momento enviándonos un correo.",
      },
    ],
  },
  terms: {
    eyebrow: "Legal",
    title: "Términos del servicio",
    subtitle: "Los términos que rigen tu uso del sitio web y los servicios de Lawyah.",
    updated: "Última actualización: julio 2025",
    sections: [
      {
        title: "Aceptación de los términos",
        body: "Al acceder a este sitio web o usar los servicios de Lawyah, aceptas estos términos. Si usas Lawyah en nombre de un bufete, declaras que estás autorizado para obligar a ese bufete.",
      },
      {
        title: "Uso del servicio",
        body: "Lawyah ofrece admisión impulsada por IA, paneles y marketing gestionado para bufetes de abogados. Aceptas usar el servicio legalmente y no intentar acceder a datos de otros bufetes.",
      },
      {
        title: "No es asesoría legal",
        body: "Lawyah es un servicio de software y marketing. Nada en este sitio web ni en las respuestas de nuestro asistente de IA constituye asesoría legal.",
      },
      {
        title: "Limitación de responsabilidad",
        body: "En la máxima medida permitida por la ley, Lawyah no es responsable de daños indirectos, incidentales o consecuentes derivados del uso del servicio.",
      },
    ],
  },
};
